import { createStore } from 'vuex'

import MapService from '@/services/MapService.ts'
import { Filters } from '@/services/Filters.ts'
import PointService from '@/services/PointService.ts'
import { sortByDistance } from "@/utils/common"

import { Panel, WindowSize, StationsStatus } from '@/constants'
import api from '@/api.ts'

/** @typedef {'map_center' | 'current_location' | 'search_place'} MapDistanceBaseType */

/**
 * 地図パネルの distanceFrom 文言
 * map.points.distance_from_label.enabled が true のときのみ基準タイプを反映する
 *
 * @param {import('vuex').Store['state']} state
 * @returns {string}
 */
function resolveMapDistanceBaseLabel(state) {
  if (!storelocator.map?.points?.distance_from_label?.enabled) {
    return storelocator.messages.common_from_map_center
  }

  const type = state.map.distanceBaseType
  if (type === 'current_location') {
    return storelocator.messages.points_distance_from_location
  }
  if (type === 'search_place') {
    return storelocator.messages.common_from_search_place
  }

  return storelocator.messages.common_from_map_center
}

/**
 * 地図パネルの距離計算に使う基準座標を返す
 * map.points.distance_from_label.enabled が true のときのみ現在地/検索地を反映する
 *
 * @param {import('vuex').Store['state']} state
 * @returns {google.maps.LatLng}
 */
function resolveMapDistanceBaseLatLng(state) {
  if (storelocator.map?.points?.distance_from_label?.enabled) {
    const type = state.map.distanceBaseType
    if (type === 'current_location' || type === 'search_place') {
      return state.map.distanceBaseLatLng || MapService.map?.getCenter()
    }
  }

  return MapService.map?.getCenter()
}

/**
 * 表示用の距離だけを基準座標から再計算する
 * 並び順は変更しない
 *
 * @param {google.maps.LatLng} baseLatLng 基準の緯度経度
 * @param {Array} points 距離を付与する店舗配列
 */
function updateDistanceFromBaseLatLng(baseLatLng, points = []) {
  if (!baseLatLng) {
    return
  }

  for (let i = 0; i < points.length; i++) {
    const p = points[i]
    const latLng =
      p?.position && typeof p.position.lat === 'function' && typeof p.position.lng === 'function'
        ? { lat: p.position.lat(), lng: p.position.lng() }
        : { lat: p.latitude, lng: p.longitude }

    p.distance = google.maps.geometry.spherical.computeDistanceBetween(baseLatLng, latLng)
  }
}

/**
 * パネル用の距離・distanceFrom を pointsInBounds から再計算する
 * selectPoint / deselectPoint 後に、直前の地図中心基準の distance が残るのを防ぐ
 *
 * @param {import('vuex').Store['state']} state
 * @param {import('vuex').Commit} commit
 */
function refreshNearPointsDistance(state, commit) {
  if (state.points.pointsInBounds?.length) {
    commit('updatePointsInBounds', state.points.pointsInBounds)
  }
}

export default createStore({
  state: {
    /**
     * WindowSize
     */
    windowSize: WindowSize.SMALL,
    /**
     * マップに関する処理が走っているかどうかを判断するためのもの
     * 1以上なら何かが進行している＝ローディング出す等する、といった使い方
     */
    mapReady: 0,
    /**
     * マップページ用をまとめていく
     */
    map: {
      /**
       * 探すパネル
       */
      panel: {
        state: Panel.States.CLOSED,
      },
      /**
       * 地図パネル距離ラベル・距離計算の基準タイプ（次の現在地/地名検索まで維持）
       * @type {MapDistanceBaseType}
       */
      distanceBaseType: 'map_center',
      /**
       * 現在地/検索地の距離計算基準座標
       * @type {google.maps.LatLng}
       */
      distanceBaseLatLng: null,
    },
    /**
     * Pointまわり
     * マップを使うページ（MapとPoint）で共通して使う
     */
    points: {
      /**
       * pointsInBounds用の取得を行ったらtrueにする
       * 店舗取得前に店舗がない表記を出したくないので、その制御用
       */
      initialized: false,
      /**
       * 地図の表示範囲内のPoint
       * 絞り込みは適用済、並び替えは未適用なので注意
       */
      pointsInBounds: [],
      /**
       * 近くのPoint
       * pointsInBoundsを地図の中心or選択中の店舗から近い順に一定件数抽出したもの
       */
      nearPoints: [],
      /**
       * 表示クラスタ内のPoint
       * TODO 中身不明
       */
      pointsInCluster: [],
      /**
       * 選択中のPoint
       * TODO 中身不明
       */
      selected: null,
      /**
       * 選択中のクラスタID
       * TODO 中身不明
       */
      selectedClusterId: null,
    },
    /**
     * Pointページ用
     */
    point: {
      /**
       * 駅まわり
       */
      stations: {
        /**
         * true: 駅の取得を無効化（海外だと取れないので無効化を推奨）
         * 国の判定はマップによるので、必要なら明示的にactionsを読んで無効化する
         */
        disabled: false,
        /**
         * 駅の取得の状況
         */
        status: StationsStatus.NONE,
        /**
         * Pointの近くの駅
         */
        stations: [],
        /**
         * ルートを表示する駅
         */
        station: null
      },
    },
    /**
     * フィルタ
     * MapとPointsで類似のことを行うのでここにまとめる、無理がでてきたら分離
     * filterIdでのURLわけを行う場合はPointでも使う可能性がある
     */
    filters: {
      /**
       * GETで渡された絞り込みなどの設定が完了したらtrueに更新される
       */
      initialized: false,
      selectedMarkers: [],
      selectedSearchConditions: [],
      query: '',
      /**
       * 選択中の追加フィールド
       */
      selectedExtraFields: {},
    },
    /**
     * 現在地
     * @type {google.maps.LatLng}
     */
    currentLocation: null,

    /** 
     * 組織固有の関数マッピング
     * designからは関数を登録したくないので、あらかじめ定義しておいた関数をキーから呼び出す
     * （現状はPontWindowのボタンイベント専用）
     * @type {Object.<string, Function>} 例 {handleClick: (point) => { alert(point.id) }}
     */
    organizationActions: {}
  },
  getters: {
    /**
     * 駅まわり取得
     * @returns state.stations
     */
    stations: state =>  {
      return state.point.stations
    },
    /**
     * 地図の中心か選択中のPointに近いPointを一定件数まで返す
     * @returns {
     *   length: {Number},
     *   items: {Array}
     * }
     */
    nearPoints: state => {
      return state.points.nearPoints
    },
    /**
     * true: ポイント選択中
     */
    isPointSelected: state => {
      return !!state.points.selected
    },
    /**
     * true: クラスタ選択中
     */
    isClusterSelected: state => {
      return state.points.pointsInCluster.length > 0
    },
    /**
     * @returns true: マップで実行中の処理がない
     */
    processible: state => {
      return state.mapReady === 0
    },
    isPanelClosed: state => {
      return state.map.panel.state === Panel.States.CLOSED
    },
    /**
     * 絞り込みを適用中か判定
     * 
     * URLでの絞り込みを行っている場合がある
     * なので、空かどうかではなく、初期状態と一致するかを見る
     * @returns {Boolean} true: 絞り込みを適用中
     */
    isFilterEnabled: state => {
      if (!state.filters.initialized) {
        return false
      }

      if (state.filters?.query) {
        // 店舗検索用のテキストが存在すれば、その時点で絞り込み状態とする
        return true
      }

      if (Filters.selectedExtraFields.isNeedApply() && Filters.selectedExtraFields.hasSelectedExtraFields()) {
        // 選択された追加フィールドで絞り込まれている
        return true
      }

      // URL指定されたマーカーを収集（配下込）
      let markerIdsByQuery = []

      storelocator.selected_markers.forEach(id => {
        markerIdsByQuery.push(id)
        
        Filters.findChildren(
          Filters.findById(Filters.TYPE_MARKERS, id)
        ).map(f => f.id).forEach(id => {
          markerIdsByQuery.push(id)
        })
      })

      // グループ配下の子が全て指定されている場合に、子が重複するため一意にする
      markerIdsByQuery = JSON.stringify([...new Set(markerIdsByQuery)].sort())

      // URLで指定された検索条件を収集（配下込）
      let searchConditionIdsByQuery = []

      storelocator.selected_search_conditions.forEach(id => {
        searchConditionIdsByQuery.push(id)
        
        Filters.findChildren(
          Filters.findById(Filters.TYPE_SEARCH_CONDITIONS, id)
        ).map(f => f.id).forEach(id => {
          searchConditionIdsByQuery.push(id)
        })
      })

      // グループ配下の子が全て指定されている場合に、子が重複するため一意にする
      searchConditionIdsByQuery = JSON.stringify([...new Set(searchConditionIdsByQuery)].sort())

      // stateに反映済のマーカーと検索条件、以下は配下込で入っている
      const markers = JSON.stringify(
        Object.keys(state.filters.selectedMarkers).reduce((mc, mid) => {
          return mc.concat(state.filters.selectedMarkers[mid])
        }, []).sort()
      )

      const searchConditions = JSON.stringify(
        Object.keys(state.filters.selectedSearchConditions).reduce((fc, fid) => {
          return fc.concat(state.filters.selectedSearchConditions[fid])
        }, []).sort()
      )

      // 比較、実体ではなく文字列化したもので見る
      // console.debug("isFilterEnabled", markers, markerIdsByQuery, searchConditions, searchConditionIdsByQuery)
      if (markers === markerIdsByQuery && searchConditions === searchConditionIdsByQuery) {
        return false
      }

      return true
    },
    /**
     * 取得済の現在地を取得
     * @returns {google.maps.LatLng}
     */
    currentLocation: state => {
      return state.currentLocation
    },
    /**
     * 選択中の店舗
     */
    selectedPoint: state => {
      return state.points.selected
    },
    /**
     * フィルタ
     * state.filters
     */
    filters: state => {
      return state.filters
    },
    
    /**
     * 組織固有の関数マッピング
     */
    organizationActions: state => {
      return state.organizationActions
    }
  },
  mutations: {
    /**
     * 駅の取得を無効化
     */
    disableStations(state) {
      state.point.stations.disabled = true
    },
    /**
     * 駅を更新
     * @param {Array} stations /api/stationsの応答まま
     */
    updateStations(state, stations) {
      state.point.stations.stations = stations
    },
    /**
     * ルートを表示する駅を更新
     */
    updateStation(state, station) {
      // console.debug("updateStation", station)
      state.point.stations.station = station
    },
    /**
     * 駅の取得状況を更新
     * @param {Number} status StationsStatus
     */
    updateStationsStatus(state, status) {
      state.point.stations.status = status
    },
    /**
     * マップの処理の積み上げ／下げ
     * @param {boolean} finished true: 処理の完了時に指定
     */
    updateMapReady(state, finished) {
      if (!finished) {
        state.mapReady ++
      } else if (state.mapReady != 0) {
        state.mapReady --
      }
    },
    updateWindowSize(state) {
      const w = window.innerWidth
      const size = w < parseInt(storelocator.breakpoints?.small)
        ? WindowSize.SMALL
        : WindowSize.LARGE

      if (state.windowSize !== size) {
        if (size === WindowSize.SMALL) {
          state.map.panel.state = Panel.States.CLOSED
        } else {
          state.map.panel.state = Panel.States.OPENED
        }
      }

      state.windowSize = size
    },
    /**
     * パネルの開閉を更新
     * @param {number} panelState Panel.States
     */
    updatePanel(state, panelState) {
      state.map.panel.state = panelState
    },
    /**
     * 選択フィルタのアップデート
     * 子孫要素がある場合、子孫も一括で更新する
     *
     * @param {Object}: {
     *   type {String}: markers | search_conditions
     *   ids {Array}: 更新する配列
     *   parent {Bool}: trueの場合、親の選択を行う
     * }
     */
    updateSelectedFilters(state, {type, id, parent}) {
      const key = type === Filters.TYPE_MARKERS
        ? "selectedMarkers"
        : "selectedSearchConditions"

      let children = []
      if (!parent && id !== Filters.BASE_MARKER_ID) {
        // フィルタの子孫要素を取得し、固定対象は更新から除外する
        children = Filters.findChildrenById(type, id)
          .filter(child => !storelocator.fixed_search_conditions.includes(child))
      }
      children.push(id)
      if (state.filters[key].includes(id)) {
        // 選択解除
        state.filters[key] = state.filters[key].filter(selectedId => {
          return !children.includes(selectedId)
        })
      } else {
        // 選択状況を更新しAnalyticsに送信
        // idがFiltersに存在しない場合は無視する
        children.forEach(id => {
          // console.debug("updateSelectedFilters", propName, id)
          if (!state.filters[key].includes(id)) {
            // パラメータ名から異なる方が集計しやすいので、冗長ながらわけておく
            if (key === 'selectedMarkers') {
              const marker = Filters.findById(Filters.TYPE_MARKERS, id)
              if(marker?.name) {
                state.filters[key].push(id)
                storelocator.analytics.client?.send('SelectMarker', {
                  'MarkerId': id,
                  'MarkerName': marker.name,
                })
              }              
            } else {
              const searchConditions = Filters.findById(Filters.TYPE_SEARCH_CONDITIONS, id)
              if(searchConditions?.name) {
                state.filters[key].push(id)
                storelocator.analytics.client?.send('SelectSearchCondition', {
                  'SearchConditionId': id,
                  'SearchConditionName': searchConditions.name,
                })
              }
            }
          }
        })
      }

      // 常時送信するパラメータを最新化
      if (storelocator.analytics.client) {
        storelocator.analytics.client.context['SearchMarkerIds'] = state.filters.selectedMarkers.join(',')
        storelocator.analytics.client.context['SearchMarkerNames'] = state.filters.selectedMarkers.map(id => {
          return Filters.findById(Filters.TYPE_MARKERS, id).name
        }).join(',')
  
        storelocator.analytics.client.context['SearchConditionIds'] = state.filters.selectedSearchConditions.join(',')
        storelocator.analytics.client.context['SearchConditionNames'] = state.filters.selectedSearchConditions.map(id => {
          return Filters.findById(Filters.TYPE_SEARCH_CONDITIONS, id).name
        }).join(',')
      }
    },
    updateQuery(state, query) {
      state.filters.query = query
    },
    /**
     * @param extraFields Filters.tsのSelectedExtraFieldsと同じ構造
     */
    updateSelectedExtraFields(state, extraFields) {
      state.filters.selectedExtraFields = { ...extraFields }
    },
    updateSelectedPoint(state, point) {
      // console.debug("updateSelectedPoint", point)
      state.points.selected = point
    },
    updateSelectedCluster(state, clusterId) {
      state.points.selectedClusterId = clusterId
      state.points.pointsInCluster = []
    },
    /**
     * 地図表示範囲内のPointを更新
     * @param {Array} points 地図表示範囲内のPoint
     */
    updatePointsInBounds(state, points) {
      // console.debug('updatePointsInBounds', points)
      state.points.pointsInBounds = points
      state.points.initialized = true

      // nearPointsの更新
      let sortBaseLatLng = null
      let distanceBaseLatLng = null
      let distanceFromLabel
      if (state.points.selected) {
        sortBaseLatLng = state.points.selected.position
        distanceBaseLatLng = state.points.selected.position
        distanceFromLabel = storelocator.messages.common_from_selected_point
      } else if (MapService.map?.getCenter()) {
        sortBaseLatLng = MapService.map.getCenter()
        distanceBaseLatLng = resolveMapDistanceBaseLatLng(state)
        distanceFromLabel = resolveMapDistanceBaseLabel(state)
      }

      const _points = state.points.pointsInBounds

      sortByDistance(sortBaseLatLng, _points)
      updateDistanceFromBaseLatLng(distanceBaseLatLng, _points)

      for (let i = 0; i < _points.length; i++) {
        const p = _points[i]
        if (distanceFromLabel) {
          p.distanceFrom = distanceFromLabel
        } else {
          delete p.distanceFrom
        }
      }

      const length = _points.length

      let items
      if (length > 99) {
        items = _points.slice(0, 100)
      } else {
        items = _points
      }

      state.points.nearPoints = {length: length, items: items}
    },
    updatePointsInCluster(state, points) {
      state.points.pointsInCluster = !points ? [] : points
    },
    /**
     * 絞り込みまわりリセット
     * Pointまわりまで反映はしないので、別途整合性を取る必要がある
     */
    resetFilter(state) {
      state.filters.selectedMarkers.splice(0)
      state.filters.selectedSearchConditions.splice(0)
      state.filters.query = ''
      if (Filters.selectedExtraFields.isNeedResetWithOtherFilters()) {
        state.filters.selectedExtraFields = {}
        Filters.selectedExtraFields.reset()
      }
    },
    /**
     * @param {google.maps.LatLng} currentLocation 現在地 
     */
    updateCurrentLocation(state, currentLocation) {
      state.currentLocation = currentLocation
    },
    /**
     * 地図パネル距離ラベル・距離計算の基準を更新する
     * @param {MapDistanceBaseType} type
     */
    setMapDistanceBase(state, { type, latLng }) {
      state.map.distanceBaseType = type
      state.map.distanceBaseLatLng = latLng || null
    },
    updateFilterInitialized(state, initialized) {
      state.filters.initialized = initialized
    },

    /**
     * 組織固有の関数マッピングを更新する
     * @param {Object.<string, Function>} actions
     */
    updateOrganizationActions(state, actions) {
      state.organizationActions = actions
    }
  },
  actions: {
    /**
     * 駅の取得を無効化
     */
    disableStations({commit}) {
      commit("disableStations")
    },
    /**
     * 駅の取得状況更新
     * @param {Object} point {latitude, longitude}
     */
    async updateStations({commit, state}, point) {
      if (state.point.stations.disabled) {
        return
      }

      commit("updateStationsStatus", StationsStatus.LOADING)

      const response = await api.fetchStations(
        point.latitude,
        point.longitude
      ).catch(e => {
        console.warn('failed fetch stations', e)
      }).finally(() => {
        commit("updateStationsStatus", StationsStatus.NONE)
      })

      if (response?.status != 200) {
        return
      }

      commit("updateStations", response.data.items)
    },
    /**
     * ルートを表示する駅を選択
     * @param {Object} station /api/stationsの応答まま
     */
    selectStation({commit}, station) {
      commit("updateStation", station)
    },
    /**
     * 現在地を保存
     * @param {google.maps.LatLng} currentLocation 現在地
     */
    saveCurrentLocation({commit}, currentLocation) {
      commit("updateCurrentLocation", currentLocation)
    },
    /**
     * 地図パネル距離ラベル・距離計算の基準を更新する（フラグ有効時のみ）
     * 既に取得済みの pointsInBounds があれば distanceFrom を再付与する
     *
     * @param {Object|MapDistanceBaseType} payload
     */
    setMapDistanceBase({ commit, state }, payload) {
      if (!storelocator.map?.points?.distance_from_label?.enabled) {
        return
      }

      const type = typeof payload === 'string' ? payload : payload.type
      const latLng = typeof payload === 'string' ? null : payload.latLng

      commit('setMapDistanceBase', { type, latLng })
      refreshNearPointsDistance(state, commit)
    },
    /**
     * フィルタの初期化完了時
     */
    onFilterInitialized({commit}) {
      commit('updateFilterInitialized', true)
    },
    /**
     * パネルを開く
     */
    openPanel({commit}) {
      commit("updatePanel", Panel.States.OPENED)
    },
    /**
     * パネルを閉じる
     */
    closePanel({commit}) {
      commit("updatePanel", Panel.States.CLOSED)
    },
    /**
     * マップ絡みの処理はじめ
     */
    startProcess({commit}) {
      commit("updateMapReady", false)
    },
    /**
     * マップ絡みの処理終わり
     */
    finishProcess({commit}) {
      commit("updateMapReady", true)
    },
    /**
     * ウィンドウサイズ変更時
     */
    onWindowResized({commit}) {
      commit("updateWindowSize")
    },
    /**
     * 表示範囲内のPointを更新
     * @param {Array} points Point[] [{..., marker, position, ...}, ...]
     */
    updatePointsInBounds({commit}, points) {
      commit("updatePointsInBounds", points)
    },
    /**
     * ポイント選択
     * 選択後に nearPoints の距離を再計算する（地名検索直後などで stale な distance が残るのを防ぐ）
     *
     * @param {Point} point 選択するポイント
     */
    selectPoint({state, commit}, point) {
      if (state.points.selected == null || state.points.selected.id !== point.id) {
        commit("updateSelectedPoint", point)
      }
      refreshNearPointsDistance(state, commit)
    },
    selectCluster({getters, commit}, cluster) {
      if (getters.isPointSelected) {
        commit("updateSelectedPoint", null)
      }

      commit("updateSelectedCluster", cluster.clusterId)
      commit("updatePointsInCluster", cluster.points)
    },
    deselectCluster({commit}) {
      commit("updateSelectedCluster", null)
    },
    deselectPoint({state, commit}) {
      MapService.pointManager.clearAll()

      commit("updateSelectedPoint", null)
      commit("updateSelectedCluster", null)
      refreshNearPointsDistance(state, commit)
    },
    /**
     * 指定の状態へのパネルの開閉
     * @param {number} {value} Panel.States
     */
    changePanel({state, commit}, {value}) {
      if (state.map.panel.state === value) {
        return
      }

      if (value < Panel.States.OPENED) {
        const dom = document.getElementById("PanelContents")
        if (!dom) {
          return
        }
    
        dom.scrollTo(0, 0)
      }

      commit("updatePanel", value)
    },
    /**
     * 絞り込み選択処理
     * @param {Object} {
     *   type {String}: markers | search_conditions
     *   id {String}: ID
     *   parent {Boole}: 子要素が全選択され、親要素を選択するときは trueを指定する
     *                  （trueの場合、子要素の選択判定をスキップする）
     *   noApply {Bool}: trueにすると内部的に状態の更新は行うが画面には反映しない
     * }
     */
    changeFilter({state, commit, dispatch}, {type, id, parent, noApply}) {
      parent = parent == undefined ? false : parent
      noApply = noApply == undefined ? false : noApply

      const propName = type === Filters.TYPE_MARKERS
        ? "selectedMarkers"
        : "selectedSearchConditions"

      if(id != null) {
        commit("updateSelectedFilters", {type: type, id: id, parent: parent})
      }

      // TODO コンポーネントに移す
      if (storelocator.page === "map") {
        PointService.closePoint()
        dispatch("deselectPoint")
      }

      const result = PointService.changeFilter(type, state.filters[propName], noApply)
      if (result) {
        commit("updatePointsInBounds", result.pointsInBounds)
      }

      PointService.updateGuide()
    },
    /**
     * 絞り込みテキストの変更
     * @param {string} text 検索文字列
     * @param {string[]} targets 検索対象 ['name', 'address' ...]
     */
    changeFilterText({ state, commit, dispatch }, { text, targets=[] }) {
      if(state.filters.query === text) {
        return
      }

      Filters.query.setQuery(text)
      Filters.query.setTargets(targets)

      commit("updateQuery", text)
      dispatch("changeFilter", { type: null, id: null })
    },
    /**
     * @param { key: string, extraField: Object }
     * key: Filters.tsのSelectedExtraFieldsのキーと同じ
     * extraField: Filters.tsのSelectedExtraFieldと同じ
     */
    changeSelectedExtraField({ commit }, { key, extraField }) {
      commit("updateSelectedExtraFields", Filters.selectedExtraFields.setSelectedExtraField(key, extraField))
    },
    /**
     * @param { patterns: Object }
     * patterns: Filters.tsのSelectedExtraFieldsと同じ
     */
    changeSelectedExtraFields({ commit }, { extraFields }) {
      commit("updateSelectedExtraFields", Filters.selectedExtraFields.setSelectedExtraFields(extraFields))
    },
    /**
     * 絞り込みをリセット
     * 
     * 初期化済である必要がある
     */
    resetFilter({ state, commit, dispatch }) {
      if (!state.filters.initialized) {
        return
      }

      dispatch("deselectPoint")
      commit("resetFilter")

      // TODO 依存関係整理したい、vuex -> serviceは不自然
      const result = PointService.resetFilter()
      commit("updatePointsInBounds", result.pointsInBounds)
    },
    /**
     * 絞り込みをリセット
     * Pointsページ用
     */
    resetFilterForPoints({ commit }) {
      commit("resetFilter")
      // Pointsページの絞り仕込みリセットはマーカー・絞り込み条件専用として設置しているため、テキスト検索のクエリはリセットさせない
      PointService.resetFilter(true, false)
    },
    /**
     * 組織固有の関数マッピングをセット
     * @param {Object} context
     * @param {Object.<string, Function>} actions
     */
    setOrganizationActions({ commit }, actions) {
      commit('updateOrganizationActions', actions)
    }
  }
})